import * as THREE from 'three';

/** high = discrete, mid = unknown / older GPU, low = iGPU / software / mobile. */
export type QualityTier = 'high' | 'mid' | 'low';

export type GpuCaps = {
  maxTextureSize: number;
  renderer: string;
  vendor: string;
};

/** Published on `window.__DS.debug.quality`. */
export type QualitySettings = {
  tier: QualityTier;
  dpr: number;
  shadows: number;
};

export const SHADOW_SIZE: Record<QualityTier, number> = {
  high: 2048,
  mid: 1024,
  low: 512,
};

export const DPR_CAP: Record<QualityTier, number> = {
  high: 2,
  mid: 1.5,
  low: 1,
};

const SOFTWARE_RE =
  /swiftshader|llvmpipe|softpipe|lavapipe|microsoft basic render|gdi generic|virtualbox|vmware/i;
const HIGH_RE =
  /rtx|geforce|gtx|quadro|tesla|\bnvidia\b|radeon rx|radeon pro|radeon vii|intel(?:\(r\))? arc|\barc a[0-9]|apple m[1-9]/i;
const ARC_RE = /intel(?:\(r\))? arc|\barc a[0-9]/i;
const IGPU_RE =
  /uhd graphics|hd graphics|iris|mali|adreno|powervr|apple gpu|imagination|radeon\(tm\) graphics|radeon graphics/i;

function blobOf(caps: GpuCaps): string {
  return `${caps.vendor} ${caps.renderer}`;
}

/** Classify fake or real WEBGL_debug_renderer_info caps. detect-gpu is not a dep. */
export function pickTier(caps: GpuCaps): QualityTier {
  const blob = blobOf(caps);
  const tex = Number.isFinite(caps.maxTextureSize) ? caps.maxTextureSize : 0;
  if (tex > 0 && tex < 4096) {
    return 'low';
  }
  if (SOFTWARE_RE.test(blob)) {
    return 'low';
  }
  if (HIGH_RE.test(blob)) {
    return tex >= 8192 ? 'high' : 'mid';
  }
  const intelIgpu = /intel/i.test(blob) && !ARC_RE.test(blob);
  if (intelIgpu || IGPU_RE.test(blob)) {
    return 'low';
  }
  if (tex >= 8192) {
    return 'mid';
  }
  return 'low';
}

/** `?tier=high|mid|low` — `igpu` aliases low for QA. */
export function parseTierOverride(search: string): QualityTier | null {
  if (!search) {
    return null;
  }
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const value = new URLSearchParams(raw).get('tier');
  if (!value) {
    return null;
  }
  const tier = value.trim().toLowerCase();
  if (tier === 'high' || tier === 'mid' || tier === 'low') {
    return tier;
  }
  if (tier === 'igpu' || tier === 'i-gpu') {
    return 'low';
  }
  return null;
}

export function settingsFor(tier: QualityTier, devicePixelRatio: number): QualitySettings {
  const raw = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  return {
    tier,
    dpr: Math.min(raw, DPR_CAP[tier]),
    shadows: SHADOW_SIZE[tier],
  };
}

function unmasked(gl: WebGLRenderingContext | WebGL2RenderingContext): {
  renderer: string;
  vendor: string;
} {
  try {
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    if (dbg) {
      return {
        renderer: String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) ?? ''),
        vendor: String(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) ?? ''),
      };
    }
  } catch {
    /* blocked or missing — fall through to masked RENDERER/VENDOR */
  }
  return {
    renderer: String(gl.getParameter(gl.RENDERER) ?? ''),
    vendor: String(gl.getParameter(gl.VENDOR) ?? ''),
  };
}

export function readCaps(renderer: THREE.WebGLRenderer): GpuCaps {
  const maxTextureSize = renderer.capabilities.maxTextureSize;
  const gl = renderer.getContext();
  if (!gl) {
    return { maxTextureSize, renderer: '', vendor: '' };
  }
  const names = unmasked(gl);
  return { maxTextureSize, renderer: names.renderer, vendor: names.vendor };
}

export function resolveQuality(
  renderer: THREE.WebGLRenderer,
  opts?: { search?: string; devicePixelRatio?: number },
): QualitySettings {
  const search =
    opts?.search ?? (typeof location !== 'undefined' ? location.search : '');
  const dpr =
    opts?.devicePixelRatio ??
    (typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
      ? window.devicePixelRatio
      : 1);
  const tier = parseTierOverride(search) ?? pickTier(readCaps(renderer));
  return settingsFor(tier, dpr);
}

/** Pin DPR + shadow map size. Leaves ACES / PCFSoftShadowMap alone. No FXAA (no EffectComposer). */
export function applyQuality(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Object3D,
  settings: QualitySettings,
): void {
  renderer.setPixelRatio(settings.dpr);
  scene.traverse((obj) => {
    if (
      !(
        obj instanceof THREE.DirectionalLight ||
        obj instanceof THREE.SpotLight ||
        obj instanceof THREE.PointLight
      )
    ) {
      return;
    }
    if (!obj.castShadow) {
      return;
    }
    obj.shadow.mapSize.set(settings.shadows, settings.shadows);
    if (obj.shadow.map) {
      obj.shadow.map.dispose();
      obj.shadow.map = null;
    }
  });
}

export class Quality {
  static detect(caps: GpuCaps, search = ''): QualityTier {
    return parseTierOverride(search) ?? pickTier(caps);
  }

  static settings(tier: QualityTier, devicePixelRatio: number): QualitySettings {
    return settingsFor(tier, devicePixelRatio);
  }

  static resolve(
    renderer: THREE.WebGLRenderer,
    opts?: { search?: string; devicePixelRatio?: number },
  ): QualitySettings {
    return resolveQuality(renderer, opts);
  }

  static apply(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Object3D,
    settings: QualitySettings,
  ): void {
    applyQuality(renderer, scene, settings);
  }
}
